import * as React from 'react'

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import type { Control, FieldValues, UseControllerProps } from 'react-hook-form'

import { Render } from '@/components/render-if'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { cn } from '@/infra'

function RadioGroup({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return <RadioGroupPrimitive.Root data-slot="radio-group" className={cn('grid gap-3', className)} {...props} />
}

function RadioGroupItem({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive aspect-square size-4 shrink-0 cursor-pointer rounded-full border border-neutral-400 bg-neutral-100 shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center text-current">
        <svg width="6" height="6" viewBox="0 0 6 6" fill="currentcolor" xmlns="http://www.w3.org/2000/svg">
          <circle cx="3" cy="3" r="3" />
        </svg>
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

type TRadioGroupFieldProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = Omit<UseControllerProps<TFieldValues>, 'control'> &
  React.ComponentProps<typeof RadioGroup> & {
    control?: Control<TFieldValues, TContext, TTransformedValues>
    options: Array<React.ComponentProps<typeof RadioGroupPrimitive.Item> & { label: string }>
    groupLabel?: string
    onChangeValue?: (value: string) => void
    errorMessageClassName?: string
  }

const RadioGroupField = <TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues>({
  name,
  control,
  options,
  defaultValue,
  groupLabel,
  onValueChange,
  errorMessageClassName,
  ...props
}: TRadioGroupFieldProps<TFieldValues, TContext, TTransformedValues>) => {
  const id = React.useId()

  const handleValueChange = (value: string, onChange: (value: string) => void) => {
    if (onValueChange) {
      onValueChange(value)
      return
    }
    onChange(value)
  }
  return (
    <FormField
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field }) => (
        <FormItem className="font-nunito">
          <FormControl>
            <div>
              <Render.If condition={groupLabel}>
                {(_groupLabel) => (
                  <FormLabel className="mb-2" aria-disabled={props.disabled} htmlFor={id} data-slot="form-label">
                    {_groupLabel}
                  </FormLabel>
                )}
              </Render.If>
              <RadioGroup
                {...props}
                {...field}
                id={id}
                onValueChange={(e) => handleValueChange(e, field.onChange)}
                defaultValue={defaultValue}
              >
                {options.map(({ label, ...p }) => (
                  <div key={p.value} className="flex items-center gap-2 p-2">
                    <RadioGroupItem
                      className="disabled:border-neutral-400 disabled:bg-neutral-100"
                      id={`${id}-${p.value}`}
                      {...p}
                    />
                    <Label
                      aria-disabled={props.disabled}
                      aria-labelledby={`${id}-${p.value}`}
                      className="text-base font-normal aria-[disabled=true]:text-neutral-500"
                      htmlFor={`${id}-${p.value}`}
                    >
                      {label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </FormControl>
          <FormMessage className={errorMessageClassName} />
        </FormItem>
      )}
    />
  )
}

export { RadioGroup, RadioGroupField, RadioGroupItem }
